import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Pencil, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Layout4 from "../DataLayouts/Layout4";
import {
  deleteApplicationRolePermissions,
  listApplicationRolePermissions,
  listApplicationRoles,
} from "../../Apis/ApplicationAdminUserApis";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import {
  isWalletAdminUser,
  WALLET_ADMIN_ACTION,
} from "../../Utils/walletAdminAccess";
import { hasPermissionAction } from "../../Utils/organizationPermissions";
import useAuthStore from "../../Zustand/Store/useAuthStore";

export default function ApplicationRolePermissionsList() {
  const navigate = useNavigate();
  const { Permission } = usePermissionDataStore();
  const { userData } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const walletAdmin = isWalletAdminUser(userData, Permission);
  const canAdd =
    walletAdmin ||
    hasPermissionAction(Permission, "USER_ROLE_PERMISSION_ADD") ||
    hasPermissionAction(Permission, WALLET_ADMIN_ACTION);
  const canEdit =
    walletAdmin ||
    hasPermissionAction(Permission, "USER_ROLE_PERMISSION_EDIT") ||
    hasPermissionAction(Permission, WALLET_ADMIN_ACTION);
  const canDelete =
    walletAdmin ||
    hasPermissionAction(Permission, "USER_ROLE_PERMISSION_DELETE") ||
    hasPermissionAction(Permission, WALLET_ADMIN_ACTION);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [perms, roles] = await Promise.all([
        listApplicationRolePermissions(),
        listApplicationRoles(),
      ]);

      const roleIdsWithPerms = new Set();
      (perms || []).forEach((p) => {
        const roleId =
          p.application_user_role_id ??
          p.applicationUserRole?.application_user_role_id;
        if (roleId) roleIdsWithPerms.add(Number(roleId));
      });

      const configured = (roles || [])
        .filter((r) => roleIdsWithPerms.has(Number(r.application_user_role_id)))
        .map((r) => ({
          id: r.application_user_role_id,
          name: r.user_role_name || r.role_name,
        }));

      setRows(configured);
    } catch (err) {
      toast.error(err.message || "Failed to load role permissions");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (row) => {
    if (!canDelete) return;
    if (
      !window.confirm(
        `Delete all permissions for role "${row.name}"? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await deleteApplicationRolePermissions(row.id);
      toast.success("Role permissions deleted");
      load();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000 }}>
      <Layout4
        heading="Role Permissions"
        subtitle="Map actions to application roles"
        showAddButton={canAdd}
        btnName="Add role permission"
        Route="/application/user-role-permission"
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography color="text.secondary" py={2}>
                      No role permissions configured yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.name}</TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        {canEdit && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/application/user-role-permission/edit/${row.id}`
                              )
                            }
                          >
                            <Pencil size={16} />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(row)}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
